import React, { useState, useEffect } from "react";
import { Pagination, Table } from "flowbite-react";
import SidbarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { FooterComponents } from "../FooterComponent";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Search, GraduationCap, Users, Loader2, Filter, Download, Award, BookOpen } from "lucide-react";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getToken } from '../../utils/auth';

export default function GrantedStudents() {
  const [grantedStudents, setGrantedStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("NON STEM");
  const [selectedAcademicYear, setSelectedAcademicYear] = useState("");
  const [academicYears, setAcademicYears] = useState([]);

  const fetchAcademicYear = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/academic/getAcademicYears"
      );
      const academicYearList = response.data;
      return academicYearList;
    } catch (error) {
      console.error("Error fetching academic years:", error);
      throw error;
    }
  };

  const { data: academicYearData } = useQuery({
    queryKey: ["academicYearKey"],
    queryFn: fetchAcademicYear,
    retry: 3,
    staleTime: 5 * 60 * 1000,
  });

  // Handle academic year data when it's available
  useEffect(() => {
    if (academicYearData && academicYearData.length > 0) {
      setAcademicYears(academicYearData);
      // Set default academic year if not already set
      if (!selectedAcademicYear) {
        const currentYear = new Date().getFullYear()-1;
        const defaultAcademicYear = academicYearData.find(ay => ay.academicYearCode === currentYear.toString()) || academicYearData[0];
        setSelectedAcademicYear(defaultAcademicYear.academicYearName);
      }
    }
  }, [academicYearData, selectedAcademicYear]);

  const fetchGrantedStudents = async () => {
    if (!selectedCategory || !selectedAcademicYear) {
      return [];
    }
    
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL +
          "hec/localLoan/getApprovedLocalLoansByCategoryAndAcademicYear",
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
          params: {
            category: selectedCategory,
            academicYear: selectedAcademicYear,
          },
        }
      );
      const data = response.data || [];
      return data;
    } catch (error) {
      console.error("Error fetching granted students:", error);
      toast.error("Failed to fetch granted students", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
      throw error;
    }
  };

  const { isPending, error, data: grantedStudentsData, refetch } = useQuery({
    queryKey: ["grantedStudents", selectedCategory, selectedAcademicYear],
    queryFn: fetchGrantedStudents,
    enabled: !!(selectedCategory && selectedAcademicYear && academicYears.length > 0),
    retry: 3,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes (replaces cacheTime)
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  // Update local state when query data changes
  useEffect(() => {
    if (grantedStudentsData) {
      setGrantedStudents(grantedStudentsData);
    } else if (error) {
      console.error("Query error:", error);
      setGrantedStudents([]);
    }
  }, [grantedStudentsData, error]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedAcademicYear, searchTerm]);

  // Refetch data when component mounts or becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedCategory && selectedAcademicYear) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch, selectedCategory, selectedAcademicYear]);

  // Filter students based on search term
  const filteredStudents = grantedStudents.filter(student =>
    student.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.nationalId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.collegeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.qualificationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.campusName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStudents = filteredStudents.slice(startIndex, endIndex);

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const formatProducts = (products) => {
    if (!products || products.length === 0) return "No products";
    return products.map(product => product.productName).join(", ");
  };

  const exportToExcel = () => {
    if (filteredStudents.length === 0) {
      toast.error("No data to export", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
      return;
    }

    // Prepare data for export with exact column headers from the image
    const excelData = filteredStudents.map((student, index) => ({
      "Registration Number": student.registrationNumber || '',
      "National ID": student.nationalId || '',
      "First Name": student.firstName ? student.firstName.trim() : '',
      "Last Name": student.lastName ? student.lastName.trim() : '',
      "Gender": student.gender || '',
      "Email": student.email || '',
      "Phone": student.phone || '',
      "Starting Year": student.startingYear || '',
      "Ending Year": student.endingYear || '',
      "Academic Year": student.academicYearId || '',
      "College ID": student.collegeId || '',
      "College Name": student.collegeName || '',
      "Qualification Code": student.qualificationCode || '',
      "Qualification Name": student.qualificationName || '',
      "Study Area": student.studyArea || '',
      "Loan Year": student.loanYearId || '',
      "Program Duration": student.programDuration || '',
      "Duration Per Year": student.durationPerYear || '',
      "Level of Study": student.levelOfStudy ? student.levelOfStudy.trim() : '',
      "Campus Name": student.campusName ? student.campusName.trim() : ''
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths for better readability
    const columnWidths = [
      { wch: 18 }, // Registration Number
      { wch: 20 }, // National ID
      { wch: 15 }, // First Name
      { wch: 15 }, // Last Name
      { wch: 10 }, // Gender
      { wch: 30 }, // Email
      { wch: 15 }, // Phone
      { wch: 12 }, // Starting Year
      { wch: 12 }, // Ending Year
      { wch: 15 }, // Academic Year
      { wch: 12 }, // College ID
      { wch: 25 }, // College Name
      { wch: 18 }, // Qualification Code
      { wch: 40 }, // Qualification Name
      { wch: 12 }, // Study Area
      { wch: 12 }, // Loan Year
      { wch: 18 }, // Program Duration
      { wch: 18 }, // Duration Per Year
      { wch: 20 }, // Level of Study
      { wch: 20 }  // Campus Name
    ];
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Granted Students');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Generate filename with current date and filters
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `Granted_Students_${selectedCategory.replace(' ', '_')}_${selectedAcademicYear}_${currentDate}.xlsx`;
    
    // Save file
    saveAs(data, filename);
    
    toast.success(`Excel file exported successfully! (${filteredStudents.length} records)`, {
      position: "top-center",
      autoClose: 5000,
      theme: "colored",
    });
  };

  const categories = ["NON STEM", "STEM"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        {/* Page Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <GraduationCap className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Granted Students</h1>
                  <p className="text-gray-600 mt-1">View all students who have been granted loans</p>
                </div>
              </div>
              <div className="mt-6 lg:mt-0 flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 rounded-xl border border-green-200">
                  <Users className="w-5 h-5 text-green-600" />
                  <span className="font-semibold">{filteredStudents.length} Granted Students</span>
                </div>
                <button 
                  onClick={exportToExcel}
                  disabled={filteredStudents.length === 0}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] ${
                    filteredStudents.length === 0 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                  } text-white`}
                >
                  <Download className="w-5 h-5" />
                  <span>Export Excel</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Filters and Search Header */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-end">
              {/* Category Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Study Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Academic Year Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Academic Year</label>
                <select
                  value={selectedAcademicYear}
                  onChange={(e) => {
                    setSelectedAcademicYear(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm"
                >
                  {academicYears &&
                    academicYears.map((academicYear) => (
                      <option
                        key={academicYear.id}
                        value={academicYear.academicYearName}
                      >
                        {academicYear.academicYearName}
                      </option>
                    ))}
                </select>
              </div>

              {/* Search Input */}
              <div className="space-y-2 lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Search Students</label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm placeholder-gray-500"
                    placeholder="Search by registration number, email, college, qualification..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="p-6">
            {isPending ? (
              <div className="flex items-center justify-center py-24">
                <div className="text-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-200 to-emerald-200 rounded-full animate-pulse mx-auto mb-4"></div>
                    <Loader2 className="w-8 h-8 text-green-600 animate-spin absolute top-4 left-1/2 transform -translate-x-1/2" />
                  </div>
                  <p className="text-gray-700 text-xl font-semibold mb-2">Loading Granted Students</p>
                  <p className="text-gray-500">Please wait while we fetch the latest data...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-24">
                <div className="w-20 h-20 bg-gradient-to-r from-red-200 to-pink-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="w-10 h-10 text-red-400" />
                </div>
                <p className="text-red-600 text-xl font-semibold mb-2">Error Loading Data</p>
                <p className="text-gray-600">Failed to load granted students. Please try again.</p>
              </div>
            ) : (
              <div>
                {currentStudents.length > 0 ? (
                  <>
                    {/* Items per page selector */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-700">Show:</span>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value={5}>5 per page</option>
                          <option value={10}>10 per page</option>
                          <option value={25}>25 per page</option>
                          <option value={50}>50 per page</option>
                        </select>
                      </div>
                      <div className="text-sm text-gray-600">
                        Showing {startIndex + 1}-{Math.min(endIndex, filteredStudents.length)} of {filteredStudents.length} students
                      </div>
                    </div>

                    {/* Enhanced Table */}
                    <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm">
                      <Table striped className="w-full">
                        <Table.Head className="bg-gradient-to-r from-gray-50 to-green-50">
                          <Table.HeadCell className="text-gray-700 font-semibold">Registration #</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">National ID</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Contact Info</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Study Period</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Institution</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Qualification</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Study Details</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Products</Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y divide-gray-200">
                          {currentStudents.map((student, index) => (
                            <Table.Row
                              className="bg-white hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-200 border-b border-gray-100"
                              key={student.nationalId || index}
                            >
                              <Table.Cell className="whitespace-nowrap">
                                <div className="font-semibold text-gray-900 text-sm">
                                  {student.registrationNumber}
                                </div>
                              </Table.Cell>
                              
                              <Table.Cell className="whitespace-nowrap">
                                <div className="font-medium text-gray-900 text-sm">
                                  {student.nationalId}
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm text-gray-900 font-medium">
                                    {student.email}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    {student.phone}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {student.startingYear} - {student.endingYear}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    Academic: {student.academicYearId}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                    {student.collegeName}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    {student.campusName}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-1">
                                  <div className="text-sm font-medium text-gray-900 max-w-xs">
                                    {student.qualificationName}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    Code: {student.qualificationCode}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="space-y-2">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    student.studyArea === 'STEM' 
                                      ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                      : 'bg-purple-100 text-purple-800 border border-purple-200'
                                  }`}>
                                    {student.studyArea}
                                  </span>
                                  <div className="text-xs text-gray-600">
                                    {student.levelOfStudy} • {student.programDuration} years
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {student.durationPerYear}
                                  </div>
                                </div>
                              </Table.Cell>

                              <Table.Cell>
                                <div className="max-w-xs">
                                  <div className="text-xs text-gray-600 space-y-1">
                                    {student.products && student.products.map((product, idx) => (
                                      <div key={idx} className="flex items-center space-x-2">
                                        <span className={`w-2 h-2 rounded-full ${
                                          product.isOnhold ? 'bg-red-400' : 'bg-green-400'
                                        }`}></span>
                                        <span className="text-xs font-medium">{product.productName}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>

                    {/* Enhanced Pagination */}
                    {totalPages > 1 && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-8 pt-6 border-t border-gray-200 space-y-4 sm:space-y-0">
                        <div className="text-sm text-gray-600 font-medium">
                          Showing page {currentPage} of {totalPages} ({filteredStudents.length} total students)
                        </div>
                        <div className="flex items-center justify-center sm:justify-end">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={onPageChange}
                            showIcons
                            theme={{
                              pages: {
                                base: "xs:mt-0 mt-2 inline-flex items-center -space-x-px",
                                showIcon: "inline-flex",
                                previous: {
                                  base: "ml-0 rounded-l-xl border border-gray-300 bg-white py-3 px-4 text-gray-500 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-200 shadow-sm",
                                },
                                next: {
                                  base: "rounded-r-xl border border-gray-300 bg-white py-3 px-4 text-gray-500 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-200 shadow-sm",
                                },
                                selector: {
                                  base: "w-12 border border-gray-300 bg-white py-3 text-gray-500 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-200 shadow-sm",
                                  active: "bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 border-green-500 shadow-md",
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-24">
                    <div className="w-20 h-20 bg-gradient-to-r from-gray-200 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <GraduationCap className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-700 text-xl font-semibold mb-2">No Granted Students Found</p>
                    <p className="text-gray-500 max-w-md mx-auto">
                      {searchTerm 
                        ? 'No students match your search criteria. Try adjusting your search terms or filters.' 
                        : `No students have been granted loans for ${selectedCategory} category in ${selectedAcademicYear}.`
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <FooterComponents />
      </div>
    </div>
  );
}